import type { FilterLine } from '../../filters/utils/describeFilterMap';

export type ContentMatch = { label: string; value: string };

/**
 * The first value in a set that holds the search text, with the filter it
 * belongs to, so a search finds a set by what it contains as well as by its
 * name. Filter names are not searched: "geo" would match nearly every set.
 */
const findContentMatch = (
  lines: FilterLine[] | null,
  query: string
): ContentMatch | null => {
  if (!lines || query.length === 0) return null;
  for (const line of lines) {
    const value = line.values.find((candidate) =>
      candidate.toLowerCase().includes(query)
    );
    if (value !== undefined) return { label: line.label, value };
  }
  return null;
};

type SearchResult<T> = {
  item: T;
  /** The value that matched, for a set found by its contents. */
  match: ContentMatch | null;
};

/**
 * The sets a search finds: sets whose name holds the text first, then the
 * rest by the filter of their first matching value, so sets matched on the
 * same filter sit together, filters in alphabetical order. A set shows once,
 * and sets matched the same way keep the list's order. An empty search
 * returns every set, as it is.
 */
export const groupSearchResults = <T>(
  candidates: { item: T; name: string; lines: FilterLine[] | null }[],
  search: string
): SearchResult<T>[] => {
  const query = search.trim().toLowerCase();
  const byName: SearchResult<T>[] = [];
  const byContents: { item: T; match: ContentMatch }[] = [];
  for (const { item, name, lines } of candidates) {
    if (name.toLowerCase().includes(query)) {
      byName.push({ item, match: null });
      continue;
    }
    const match = findContentMatch(lines, query);
    if (match) byContents.push({ item, match });
  }
  // Array sort is stable, so sets on the same filter keep the list's order.
  byContents.sort((a, b) => a.match.label.localeCompare(b.match.label));
  return [...byName, ...byContents];
};
